import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Globe, Users, Smartphone, Link2, BarChart, Zap, Calendar, Wallet } from 'lucide-react';
import SkeletonLoading from '../../components/Skeleton';

const AdDetail = () => {
  const { adId } = useParams();
  const id = parseInt(adId || '', 10);
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);

  // Datos de ejemplo
  const ads = [
    {
      _id: 1,
      title: "Summer Sale Campaign",
      image: "https://via.placeholder.com/800x400",
      link: "https://example.com/summer-sale",
      description: "Annual summer promotion with special discounts",
      isActive: true,
      clicks: 2450,
      impressions: 10200,
      ctr: 2.4,
      engagementRate: 18.7,
      weight: 4.2,
      startDate: "2024-06-01",
      endDate: "2024-08-31",
      budget: 5000,
      adType: "banner",
      userSegment: "Premium Users",
      allowedCountries: ["US", "CA", "MX"],
      allowPublish: true,
      activeHours: [9, 21],
      clickData: {
        countryCode: "US",
        clickCountry: "United States",
        clickRegion: "California",
        deviceType: "Mobile",
        browser: "Chrome",
        os: "Android",
        referrer: "https://socialmedia.com",
        isMobile: true,
        isDesktop: false,
        isBot: false
      }
    },
    // Agrega más anuncios aquí...
  ];

  useEffect(() => {
    setTimeout(() => {
      const selectedAd = ads.find(ad => ad._id === id);
      setAd(selectedAd);
      setLoading(false);
    }, 1000);
  }, [id]);

  if (loading) return <SkeletonLoading />;
  if (!ad) return <div className="p-6 text-center">Ad not found</div>;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Link to="/ads" className="mb-6 inline-flex items-center text-navy-900 hover:text-navy-700">
        <ArrowLeft size={18} className="mr-2" />
        Back to Campaigns
      </Link>

      {/* Header Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy-100 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          <img 
            src={ad.image} 
            alt={ad.title} 
            className="w-full md:w-1/3 rounded-xl object-cover h-64"
          />
          
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <h1 className="text-3xl font-bold text-navy-900">{ad.title}</h1>
              <span className={`px-3 py-1 rounded-full text-sm ${ad.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {ad.isActive ? 'Active' : 'Paused'}
              </span>
            </div>
            
            <p className="text-gray-600 mb-6">{ad.description}</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Zap} label="Ad Type" value={ad.adType} />
              <StatCard icon={Wallet} label="Budget" value={`$${ad.budget.toLocaleString()}`} />
              <StatCard icon={BarChart} label="Engagement Rate" value={`${ad.engagementRate}%`} />
              <StatCard icon={Globe} label="Weight" value={ad.weight.toFixed(1)} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Performance Metrics */}
          <SectionCard icon={BarChart} title="Performance Metrics">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricBox label="Total Clicks" value={ad.clicks} />
              <MetricBox label="Impressions" value={ad.impressions} />
              <MetricBox label="CTR" value={`${ad.ctr}%`} />
              <div className="col-span-full">
                <div className="bg-navy-50 p-4 rounded-xl">
                  <h4 className="text-sm font-semibold text-navy-900 mb-2">Engagement Progress</h4>
                  <div className="w-full bg-white rounded-full h-2">
                    <div 
                      className="bg-navy-900 rounded-full h-2 transition-all" 
                      style={{ width: `${Math.min(ad.engagementRate, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Click Analytics */}
          <SectionCard icon={Smartphone} title="Click Analytics">
            <div className="grid md:grid-cols-2 gap-4">
              <InfoItem label="Primary Country" value={ad.clickData.clickCountry} />
              <InfoItem label="Region" value={ad.clickData.clickRegion} />
              <InfoItem label="Device Type" value={ad.clickData.deviceType} />
              <InfoItem label="Browser" value={ad.clickData.browser} />
              <InfoItem label="OS" value={ad.clickData.os} />
              <InfoItem label="Referrer" value={ad.clickData.referrer} />
            </div>
          </SectionCard>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Campaign Details */}
          <SectionCard icon={Calendar} title="Campaign Dates">
            <div className="space-y-3">
              <InfoItem icon={Clock} label="Start Date" value={new Date(ad.startDate).toLocaleDateString()} />
              <InfoItem icon={Clock} label="End Date" value={new Date(ad.endDate).toLocaleDateString()} />
              <div className="pt-3 border-t border-navy-100">
                <InfoItem label="Active Hours" value={`${ad.activeHours[0]}:00 - ${ad.activeHours[1]}:00`} />
              </div>
            </div>
          </SectionCard>

          {/* Targeting */}
          <SectionCard icon={Users} title="Targeting">
            <div className="space-y-3">
              <InfoItem label="User Segment" value={ad.userSegment} />
              <div>
                <span className="text-sm text-navy-900 font-medium">Allowed Countries</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ad.allowedCountries.map(country => (
                    <span key={country} className="px-2 py-1 bg-navy-50 text-navy-900 rounded-md text-sm">
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-2xl border border-navy-100 shadow-sm">
            <h3 className="text-lg font-semibold text-navy-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-3 bg-navy-50 rounded-xl hover:bg-navy-100">
                <span className="text-navy-900">Set Budget</span>
                <Wallet size={18} className="text-navy-900" />
              </button>
              <button className="w-full flex items-center justify-between p-3 bg-navy-50 rounded-xl hover:bg-navy-100">
                <span className="text-navy-900">View Ad Preview</span>
                <Link2 size={18} className="text-navy-900" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componentes auxiliares
const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="bg-white p-6 rounded-2xl border border-navy-100 shadow-sm">
    <div className="flex items-center mb-4">
      <Icon size={20} className="text-navy-900 mr-2" />
      <h2 className="text-xl font-semibold text-navy-900">{title}</h2>
    </div>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-navy-50 p-4 rounded-xl">
    <Icon size={20} className="text-navy-900 mb-2" />
    <div className="text-sm text-navy-900">{label}</div>
    <div className="text-xl font-bold text-navy-900">{value}</div>
  </div>
);

const MetricBox = ({ label, value }) => (
  <div className="bg-white p-4 rounded-xl border border-navy-100">
    <div className="text-sm text-navy-900">{label}</div>
    <div className="text-2xl font-bold text-navy-900">{value}</div>
  </div>
);

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center text-navy-900">
      {Icon && <Icon size={16} className="mr-2" />}
      <span className="text-sm">{label}</span>
    </div>
    <span className="text-navy-900 font-medium">{value || '-'}</span>
  </div>
);

export default AdDetail;